/**
 * 선지 자동 생성 파이프라인 — 리비전 지문과 응답 적용.
 * 슬롯은 인덱스가 아니라 **id** 로 다닌다. index 는 적용 시점에 `target.quiz` 에서만 태어난다
 * (applyOptionFill → slotsOf). 리프 규칙은 applyOptionFill · resolveAnswerSlotToMark · writeOptionFills 에 있다.
 */
import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import {
  type ActionTarget,
  type ApplyOutcome,
  type FormWriter,
} from '../types/questionCardAi.types';

import { applyOptionFill } from './applyOptionFill';
import { type FillOptionsResponseDto } from './questionCardAiEndpoints';
import {
  defineRevision,
  REVISION_FIELDS,
  type RevisionOf,
} from './questionCardAiRevision';
import {
  isSlotFilled,
  slotSchemaOf,
  slotsOf,
} from './questionCardAiSlotSchema';
import {
  needsServerAnswerPick,
  resolveAnswerSlotToMark,
} from './resolveAnswerSlotToMark';
import { writeAnswerMarks, writeOptionFills } from './writeOptionFills';

/**
 * 요청 시점에 "이미 값이 있던" 슬롯과 보호 자리(pinned — 정답 표시·단답 0번)의 key. 서버는
 * 이것들을 맥락으로 생성하므로 응답이 왔을 때 그대로여야 적용한다. 빈 오답 슬롯은 넣지 않는다
 * — 사용자가 요청 중 빈 칸 하나를 채우면 그 슬롯만 건너뛰는 것이 applyOptionFill 의 설계다.
 * **집합은 요청 시점 문항으로 고정**한다. 적용 시점 문항으로 다시 고르면 사용자가 방금
 * 채운 빈 칸이 보호 슬롯으로 승격돼 응답 전체가 폐기된다.
 */
const protectedKeys = (requested: BuildQuizListItem): Set<string> => {
  const schema = slotSchemaOf(requested.type);
  if (!schema) return new Set();
  return new Set(
    schema
      .read(requested)
      .filter((slot, index) => isSlotFilled(slot) || schema.pinned(slot, index))
      .map(slot => slot.key),
  );
};

/** 보호 슬롯의 **현재** 값. 삭제된 슬롯은 빠져서 자연히 지문이 달라진다. */
const protectedSnapshot = (quiz: BuildQuizListItem, keys: Set<string>) =>
  slotsOf(quiz).filter(slot => keys.has(slot.key));

/**
 * 선지 채움용 지문 팩토리 — **요청 시점에** 보호 슬롯 집합을 굳히고, 이후 호출마다
 * 현재 문항의 값을 그 key 들로만 직렬화한다(문제문 + 보호 슬롯 + builderContext).
 * 팩토리인 이유: getValues 가 돌려준 문항은 setValue 로 제자리 변경되므로, 적용 시점에
 * "요청 시점 문항" 을 다시 읽을 방법이 없다. 기준은 지금 당장 계산해 닫아 둬야 한다.
 */
export const fillRevisionFor = (requested: BuildQuizListItem): RevisionOf => {
  const keys = protectedKeys(requested);
  // 빈 슬롯 편집은 허용한다 — 보호 슬롯만 본다. 서버가 맥락으로 보는 문제 이미지·해설·
  // 난이도·성취기준도 바뀌면 결과가 무의미하다.
  return defineRevision({
    question: REVISION_FIELDS.question,
    questionImage: REVISION_FIELDS.questionImage,
    sourceContext: REVISION_FIELDS.sourceContext,
    protectedSlots: quiz => protectedSnapshot(quiz, keys),
    // 보호 슬롯 밖의 빈 선지에 정답 표시를 더하거나 빼는 것도 변경이다 — 후보 모드에서
    // 그 슬롯에 AI 오답이 쓰이고, 교사의 빈 정답 선택이 기본값으로 오인돼 지워진다.
    answerKeys: quiz =>
      slotsOf(quiz)
        .filter(slot => slot.isAnswer)
        .map(slot => slot.key),
    builderContext: REVISION_FIELDS.builderContext,
  });
};

/**
 * fill 응답을 폼에 쓴다. 정답이 아직 없던 카드에만 AI 가 지목한 자리를 정답으로 세운다
 * (교사가 이미 고른 정답은 절대 옮기지 않는다). 리비전 가드는 호출자(runner)가 이미 통과시켰다.
 */
export const applyFillOptionsResponse = (
  {
    fills,
    answer_slot_ids,
  }: Pick<FillOptionsResponseDto, 'fills' | 'answer_slot_ids'>,
  { quiz, index: quizIndex }: ActionTarget,
  writer: FormWriter,
): ApplyOutcome => {
  const hasServerAnswerPick = (answer_slot_ids?.length ?? 0) > 0;
  // 후보 모드인데 서버가 정답을 지목하지 않았으면, 오답만 쓰고 빈 기본 정답은 건너뛰어 정답이
  // 빈 채 성공 처리된다(저장 불가 문항). 쓰기 전에 폐기한다.
  const isCandidateMode =
    !!slotSchemaOf(quiz.type)?.acceptsAnswerPick && needsServerAnswerPick(quiz);
  if (isCandidateMode && !hasServerAnswerPick)
    return { discarded: 'answer_pick_missing', appliedCount: 0 };
  const { filled } = applyOptionFill(quiz, fills, hasServerAnswerPick);
  // 한 칸도 못 썼으면 성공이 아니다 — 하이라이트도, success 계측도 하지 않는다.
  if (filled.length === 0)
    return { discarded: 'no_fills_applied', appliedCount: 0 };

  // 판정은 **쓰기 전에** 한다. getValues 가 돌려준 quiz 는 setValue 로 제자리 변경되므로,
  // 쓴 뒤에 보면 기본 정답 칸에 생긴 AI 텍스트를 교사의 선택으로 오인한다.
  const decision = resolveAnswerSlotToMark(quiz, answer_slot_ids, filled);
  // 후보 모드(정답 내용이 없어 서버가 정답까지 지목)인데 지목 자리 중 **하나라도** 찍을 수
  // 없으면 — 요청 중 교사가 그 빈 칸을 직접 채웠다 — 나머지만 쓰면 빈 기본 정답에 AI 오답이
  // 남거나 복수 정답 일부가 빠진 채 성공 처리된다. 응답 전체를 폐기한다.
  const filledIds = new Set(filled.map(write => write.slotId));
  const answerPickLost =
    isCandidateMode &&
    !!answer_slot_ids?.some(slotId => !filledIds.has(slotId));
  if (answerPickLost) return { discarded: 'source_changed', appliedCount: 0 };
  writeOptionFills(writer.setValue, quiz, quizIndex, filled);
  if (decision) writeAnswerMarks(writer.setValue, quiz, quizIndex, decision);

  writer.highlight([quiz.id]);
  return { discarded: null, appliedCount: filled.length };
};
